import React, { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LineExclamationCircleIcon from 'LuxKit/Icons/line/LineExclamationCircleIcon'
import LineExclamationTriangleIcon from 'LuxKit/Icons/line/LineExclamationTriangleIcon'
import PrivateRequestFormModal from '../PrivateRequest/PrivateRequestFormModal'
import ModalContext from 'contexts/ModalContext'
import TextLink from 'LuxKit/TextLink'
import Group from 'components/utils/Group'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import RequestAgentCallbackModal from 'supportAssistant/components/RequestAgentCallback/Modals/RequestAgentCallbackModal'
import TextButton from 'LuxKit/Button/TextButton'
import ContactContext from 'contexts/contactContext'
import LineIncomingCallIcon from 'LuxKit/Icons/line/LineIncomingCallIcon'

interface Props {
  type: 'soldOut' | 'limited'
  variant: 'small' | 'medium' | 'large'
  offer: App.Tours.TourV2Offer
  hasPrivateRequest: boolean
  isPackageOptionTile?: boolean
  travellers: Array<App.Occupants>
}

function TourV2DepartureCta(props: Props) {
  const {
    type,
    offer,
    hasPrivateRequest,
    isPackageOptionTile = false,
    variant,
    travellers,
  } = props

  const intl = useIntl()
  const supportContact = useContext(ContactContext)
  const contact = supportContact.defaultContact

  const callbacksFeatureEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.toursRequestAgentCallbackEnabled)

  const showModal = useContext(ModalContext)

  const handleCallbackClick = () => { showModal(<RequestAgentCallbackModal type="TOURS" />) }

  const toggleModal = useCallback(() => {
    showModal(<PrivateRequestFormModal offer={offer} defaultTravellers={travellers} />)
  }, [showModal, offer, travellers])

  const soldOutMessagePackage = intl.formatMessage({
    id: 'tQJzpfQcWs',
    defaultMessage: 'This departure is sold out. Call us for more tour options.',
    description: 'Sold out message for package option tile',
  })

  const soldOutMessageDeparture = intl.formatMessage({
    id: 'Tu4X4TxSev',
    defaultMessage: 'This departure is sold out. Try a different departure or call us for more tour options.',
    description: 'Sold out message for departure tile',
  })

  const limitedMessagePackage = intl.formatMessage(
    {
      id: 'Wkix+JG4Ea',
      defaultMessage: 'This departure has <bold>limited availability</bold>. Call us for more options.',
      description: 'Limited availability message for package option tile',
    },
    {
      bold: (chunks: React.ReactNode) => <b key="bold">{chunks}</b>,
    },
  )

  const limitedMessageDeparture = intl.formatMessage(
    {
      id: 'IOo5E/cIEy',
      defaultMessage: 'This departure has <bold>limited availability</bold>. Try a different departure or call us for more options.',
      description: 'Limited availability message for departure tile',
    },
    {
      bold: (chunks: React.ReactNode) => <b key="bold">{chunks}</b>,
    },
  )

  return <Group gap={12} desktopDirection="vertical" direction="vertical" fullWidth>
    {type === 'soldOut' &&
      <BodyTextBlock variant={variant} colour="neutral-two" startIcon={<LineExclamationCircleIcon />}>
        {isPackageOptionTile ? soldOutMessagePackage : soldOutMessageDeparture}
      </BodyTextBlock>}

    {type === 'limited' &&
      <BodyTextBlock variant={variant} colour="neutral-two" startIcon={<LineExclamationTriangleIcon />}>
        {isPackageOptionTile ? limitedMessagePackage : limitedMessageDeparture}
      </BodyTextBlock>}
    <Group desktopDirection="horizontal-reverse" direction="vertical" gap={12} verticalAlign="center" horizontalAlign="center" desktopHorizontalAlign="space-between" fullWidth>

      <TextButton
        fit={isPackageOptionTile ? 'full-width' : 'mobile-full-width'}
        size="medium"
        kind="secondary"
        href={`tel:${contact.number}`}
      >
        {intl.formatMessage(
          {
            id: 'JayFIblT6u',
            defaultMessage: 'Call on {contactNumber}',
            description: 'Button text to call contact',
          },
          { contactNumber: contact.humanReadable },
        )}
      </TextButton>
      {callbacksFeatureEnabled && <TextLink
        size="medium"
        weight="bold"
        onClick={handleCallbackClick}
        startIcon={<LineIncomingCallIcon />}
      >
        {intl.formatMessage({
          id: 'AlLZOgfDyc',
          defaultMessage: 'Request a callback',
          description: 'Link text to request callback',
        })}
      </TextLink>}
      {hasPrivateRequest && <TextLink size={variant} weight="regular" onClick={toggleModal}>
        {intl.formatMessage({
          id: 'fC6lOUQr2k',
          defaultMessage: 'Request private departure',
          description: 'Link text to request private departure',
        })}
      </TextLink>}
    </Group>
  </Group>
}

export default TourV2DepartureCta
